/* eslint-disable react/no-array-index-key */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronLeft from "@sunSource/Icons/ChevronLeft";
import ChevronRight from "@sunSource/Icons/ChevronRight";
import useEmblaCarousel from  "embla-carousel-react";
import React, { FC } from "react";
import { css, ThemeProps, withTheme } from "styled-components";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";

interface SlideModel {
    fields: {
        DateTitle: string;
        TimelineTitle: string;
        image: string;
        description: string;
        link: LinkFieldValue;
        linkTitle: string;
    };
}

const enum fields {
    slides = "slides",
    slidesPerRow = "slidesPerRow",
    title = "title",
    hideCompanyData = "hideCompanyData"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slides]: SlideModel[];
        [fields.slidesPerRow]: number;
        [fields.title]: string;
        [fields.hideCompanyData]: boolean;
    };
    extendedStyles?: SlideshowStyles;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;
export interface SlideshowStyles {
    wrapper?: InjectableCss;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    mainWrapper?: InjectableCss;
    dateTitleStyle?: TypographyPresentationProps;
    timelineTitleStyle?: TypographyPresentationProps;
    descriptionStyle?: TypographyPresentationProps;
    verticalLine?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    wrapper: {
        css: css`
            margin: 50px auto 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 720px !important;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0px;
                            flex-wrap: nowrap;
                        `,
                        css`
                            max-width: 1300px !important;
                            flex-wrap: nowrap;
                            padding: 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    mainWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 5px 35px 5px 35px;
                        `,
                        null,
                        null,
                        css`
                            padding: 0px 95px 0px 95px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            border-radius: 50%;
            background: transparent;
            left: -9px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                width: 30px;
                height: 30px;
            }
            border: 0;
            padding: 0;
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            right: -9px;
            border-radius: 50%;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                //stroke: white;
                width: 30px;
                height: 30px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding: 20px 30px 0;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            background: transparent;
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            border-radius: 12px;
            padding: 0 10px;
        `,
    },
    carouselSlideInner: {
        css: css`
            [data-test-selector="productImage"] {
                min-height: 150px;
                max-height: 150px;
                height: 150px;
            }
            width: 100%;
            height: 100%;
            position: relative;
            padding: 15px 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            [data-test-selector="productImage"] ~ div {
                margin: 15px 0 7px 0;
                [class^="TypographyStyle"] {
                    font-size: 14px;
                }
            }
            [data-test-selector="productDescriptionLink"] > span {
                font-size: 15px;
                text-align: center;
                color: ${getColor("text.accent")};
                font-weight: 500;
                &:hover {
                    color: ${getColor("text.accent")};
                }
            }
        `,
    },
    carouselProductStyles: {
        productBrandStyles: {
            nameLink: {
                css: css`
                    [class^="TypographyStyle"] {
                        font-weight: 400 !important;
                    }
                `,
            },
        },
        productImageStyles: {
            linkWrappingImage: {
                css: css`
                    display: flex;
                    justify-content: center;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 150px;
                    width: 150px;
                `,
            },
        },
    },
    dateTitleStyle: {
        css: css`
            font-family: Barlow-Bold;
            color: #00539B;
            text-transform: uppercase;
        `
    },
    timelineTitleStyle: {
        css: css`
            font-family: Barlow-Bold;
        `
    },
    descriptionStyle: {
        css: css`
            padding-top: 5px;
            font-family: SourceSans3-Regular;
        `
    },
    verticalLine: {
        css: css`
            border-left: 2px solid #B1B0B5;
            height: 60px;
        `
    },
};
const styles = slideshowStyles;
export const OurStoryCarousal: FC<Props> = ({ fields, shellContext, theme }) => {

    const desktop = parseInt(`${fields.slidesPerRow}`, 10);
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = fields.slidesPerRow || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.slidesPerRow]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.slidesPerRow, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !fields.slides.length) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
             watchDrag: isTouchDevice(),
            loop: fields.slides.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, fields.slides]);

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.mainContainer}>
                    <GridItem {...styles.prevArrowGridItem}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    </GridItem>

                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                    {fields.slides.map((item: SlideModel, index: number) => (
                                        <StyledWrapper
                                            {...styles.carouselSlide}
                                            key={`${item.fields.DateTitle}${index}`}
                                            style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        >
                                            <StyledWrapper
                                                {...styles.carouselSlideInner}
                                                data-test-selector="carousel_slide"
                                            >
                                                <Typography {...styles.dateTitleStyle}>{item.fields.DateTitle}</Typography>
                                                <LazyImage
                                                    src={item.fields.image}
                                                    height="auto"
                                                />
                                                <StyledWrapper {...styles.verticalLine}></StyledWrapper>
                                                <Typography {...styles.timelineTitleStyle}>{item.fields.TimelineTitle}</Typography>
                                                <Typography {...styles.descriptionStyle}>{item.fields.description}</Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>

                    <GridItem {...styles.nextArrowGridItem}>
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withTheme(OurStoryCarousal)),
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.slidesPerRow,
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                displayName: "Slides Per Row(Min 2 & max 5) ",
                tab: advancedTab,
                min: 2,
                max: 5,
                placeholder: "Minumun 2 & Maximun 5",
            },

            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
                tab: contentTab,
                fieldType: "Translatable",
            },

            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { DateTitle } }) => DateTitle || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                hideAdd: false,

                fieldDefinitions: [
                    {
                        name: "DateTitle",
                        editorTemplate: "TextField",
                        displayName: "Date Title",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        displayName: "Comapny Logo",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "TimelineTitle",
                        editorTemplate: "TextField",
                        displayName: "Timeline Title",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
